/*
 * SkillBuilder.tsx — D22: Custom Skill Builder
 * API: GET/POST /api/v1/skills/registry/...
 * Pattern: mirrors DetectionRules.tsx (3-state view, dynamic conditions, inline styles)
 */
import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, safeFetch } from '../constants';

// ── Types ──
interface SkillDef {
  id: number;
  skill_code: string;
  skill_name: string;
  category: string;
  execution_mode: string;
  source: string;
  org_id: number | null;
  version: number;
  status: string;
  description: string;
  execution_count: number;
  success_rate: number | null;
}

interface TemplateParam {
  type: string;
  required: boolean;
  description: string;
  default?: any;
  enum?: string[];
}

interface Template {
  template_id: string;
  name: string;
  description: string;
  category: string;
  parameters: Record<string, TemplateParam>;
}

interface SkillVersion {
  version: number;
  change_type: string;
  change_notes: string | null;
  changed_by: number | null;
  created_at: string | null;
}

interface Condition {
  field: string;
  operator: string;
  value: any;
}

// ── Constants ──
const VALID_OPERATORS = [
  { value: 'GT', label: '> (Greater Than)' },
  { value: 'LT', label: '< (Less Than)' },
  { value: 'GTE', label: '>= (Greater or Equal)' },
  { value: 'LTE', label: '<= (Less or Equal)' },
  { value: 'EQ', label: '= (Equals)' },
  { value: 'NEQ', label: '!= (Not Equals)' },
  { value: 'IN', label: 'IN (One Of)' },
  { value: 'NOT_IN', label: 'NOT IN' },
  { value: 'CONTAINS', label: 'Contains' },
  { value: 'STARTS_WITH', label: 'Starts With' },
  { value: 'IS_NULL', label: 'Is Empty' },
  { value: 'IS_NOT_NULL', label: 'Is Not Empty' },
  { value: 'OLDER_THAN_DAYS', label: 'Older Than N Days' },
  { value: 'NEWER_THAN_DAYS', label: 'Newer Than N Days' },
];

const SOURCE_TABLES = [
  'canonical_ap_invoices', 'canonical_ap_invoice_lines',
  'canonical_goods_receipts', 'canonical_purchase_orders',
  'canonical_ar_invoices', 'canonical_bank_statements',
  'canonical_assets', 'canonical_ic_transactions',
  'canonical_budgets', 'canonical_vendors', 'canonical_customers',
];

const CATEGORIES = ['DETECT', 'VALIDATE', 'MATCH', 'CALCULATE'];

const CAT_COLORS: Record<string, string> = {
  DETECT: 'var(--navy-700)', VALIDATE: 'var(--positive)', MATCH: '#a855f7', CALCULATE: '#f97316',
};

// ── Styles (copied from DetectionRules.tsx) ──
const inputStyle: React.CSSProperties = {
  padding: '6px 10px', fontSize: 13, border: '1px solid var(--border-default)',
  borderRadius: 6, background: 'var(--bg-card)', color: 'inherit', width: '100%',
};
const selectStyle: React.CSSProperties = { ...inputStyle, cursor: 'pointer' };
const btnStyle: React.CSSProperties = {
  padding: '6px 14px', fontSize: 12, fontWeight: 600,
  border: '1px solid var(--border-default)', borderRadius: 6,
  cursor: 'pointer', background: 'transparent', color: 'inherit',
};
const labelStyle: React.CSSProperties = {
  fontSize: 11, color: 'var(--text-tertiary)', display: 'block', marginBottom: 4,
};

export default function SkillBuilder() {
  // ── State: lists ──
  const [skills, setSkills] = useState<SkillDef[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMsg, setActionMsg] = useState('');

  // ── State: views ──
  const [selectedSkill, setSelectedSkill] = useState<SkillDef | null>(null);
  const [versions, setVersions] = useState<SkillVersion[]>([]);
  const [showBuilder, setShowBuilder] = useState(false);
  const [builderMode, setBuilderMode] = useState<'template' | 'custom'>('template');

  // ── State: builder form ──
  const [formTemplateId, setFormTemplateId] = useState('');
  const [formSkillCode, setFormSkillCode] = useState('');
  const [formSkillName, setFormSkillName] = useState('');
  const [formCategory, setFormCategory] = useState('DETECT');
  const [formDescription, setFormDescription] = useState('');
  const [formParams, setFormParams] = useState<Record<string, any>>({});
  const [formSourceTable, setFormSourceTable] = useState('');
  const [formConditions, setFormConditions] = useState<Condition[]>([
    { field: 'amount', operator: 'GT', value: 0 },
  ]);
  const [formLogic, setFormLogic] = useState('AND');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // ── Data loading ──
  const load = useCallback(() => {
    setLoading(true);
    setError('');
    Promise.all([
      safeFetch(`${API_V1}/skills/registry/?source=CLIENT&limit=100`, { skills: [] }),
      safeFetch(`${API_V1}/skills/registry/templates`, { templates: [] }),
    ])
      .then(([sData, tData]) => {
        setSkills(Array.isArray(sData?.skills) ? sData.skills : []);
        setTemplates(Array.isArray(tData?.templates) ? tData.templates : []);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadVersions = (skillCode: string) => {
    safeFetch(`${API_V1}/skills/registry/${skillCode}/versions`, { versions: [] })
      .then(data => setVersions(data?.versions || []))
      .catch(() => setVersions([]));
  };

  // ── View switching ──
  const openDetail = (skill: SkillDef) => {
    setSelectedSkill(skill);
    setShowBuilder(false);
    loadVersions(skill.skill_code);
  };

  const openTemplateBuilder = (templateId: string) => {
    const tpl = templates.find(t => t.template_id === templateId);
    resetForm();
    setBuilderMode('template');
    setFormTemplateId(templateId);
    if (tpl) {
      setFormSkillName(tpl.name);
      setFormCategory(tpl.category);
      // Pre-fill defaults
      const defaults: Record<string, any> = {};
      for (const [key, param] of Object.entries(tpl.parameters)) {
        if (param.default !== undefined) defaults[key] = param.default;
      }
      setFormParams(defaults);
    }
    setShowBuilder(true);
    setSelectedSkill(null);
  };

  const openCustomBuilder = () => {
    resetForm();
    setBuilderMode('custom');
    setShowBuilder(true);
    setSelectedSkill(null);
  };

  const openEdit = (skill: SkillDef) => {
    resetForm();
    setEditingId(skill.id);
    setFormSkillCode(skill.skill_code);
    setFormSkillName(skill.skill_name);
    setFormCategory(skill.category);
    setFormDescription(skill.description);
    setBuilderMode('custom');
    setShowBuilder(true);
    setSelectedSkill(null);
  };

  const resetForm = () => {
    setEditingId(null);
    setFormTemplateId('');
    setFormSkillCode('');
    setFormSkillName('');
    setFormCategory('DETECT');
    setFormDescription('');
    setFormParams({});
    setFormSourceTable('');
    setFormConditions([{ field: 'amount', operator: 'GT', value: 0 }]);
    setFormLogic('AND');
    setFormErrors([]);
  };

  // ── Form helpers ──
  const updateCondition = (idx: number, key: string, val: any) => {
    const updated = [...formConditions];
    (updated[idx] as any)[key] = val;
    setFormConditions(updated);
  };

  const addCondition = () => {
    setFormConditions([...formConditions, { field: 'amount', operator: 'GT', value: 0 }]);
  };

  const removeCondition = (idx: number) => {
    if (formConditions.length <= 1) return;
    setFormConditions(formConditions.filter((_, i) => i !== idx));
  };

  // ── Save ──
  const handleSave = async () => {
    setFormErrors([]);
    setSaving(true);

    try {
      let result;

      if (editingId) {
        // Update existing
        const res = await authFetch(`${API_V1}/skills/registry/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            skill_name: formSkillName || undefined,
            description: formDescription || undefined,
            execution_config: {
              source_table: formSourceTable,
              conditions: formConditions,
              condition_logic: formLogic,
            },
            change_notes: 'Updated via Skill Builder',
          }),
        });
        result = await res.json();
        if (!res.ok) throw new Error(result?.detail?.message || result?.detail?.validation_errors?.join(', ') || `Error ${res.status}`);

      } else if (builderMode === 'template' && formTemplateId) {
        // Template instantiation
        if (!formSkillCode.trim()) { setFormErrors(['Skill Code is required']); setSaving(false); return; }
        if (!formSkillName.trim()) { setFormErrors(['Skill Name is required']); setSaving(false); return; }

        // Validate required params
        const tpl = templates.find(t => t.template_id === formTemplateId);
        if (tpl) {
          const missingParams: string[] = [];
          for (const [key, param] of Object.entries(tpl.parameters)) {
            if (param.required && (formParams[key] === undefined || formParams[key] === '')) {
              missingParams.push(key);
            }
          }
          if (missingParams.length) {
            setFormErrors(missingParams.map(k => `Required parameter: ${k}`));
            setSaving(false);
            return;
          }
        }

        // Process array params
        const processedParams = { ...formParams };
        if (tpl) {
          for (const [key, param] of Object.entries(tpl.parameters)) {
            if (param.type === 'array' && typeof processedParams[key] === 'string') {
              processedParams[key] = processedParams[key].split(',').map((s: string) => s.trim()).filter(Boolean);
            }
            if ((param.type === 'number' || param.type === 'integer') && typeof processedParams[key] === 'string') {
              processedParams[key] = Number(processedParams[key]);
            }
          }
        }

        const res = await authFetch(`${API_V1}/skills/registry/from-template`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            template_id: formTemplateId,
            skill_code: formSkillCode,
            skill_name: formSkillName,
            parameters: processedParams,
          }),
        });
        result = await res.json();
        if (!res.ok) throw new Error(result?.detail?.message || result?.detail?.validation_errors?.join(', ') || `Error ${res.status}`);

      } else {
        // Custom creation
        if (!formSkillCode.trim()) { setFormErrors(['Skill Code is required']); setSaving(false); return; }
        if (!formSkillName.trim()) { setFormErrors(['Skill Name is required']); setSaving(false); return; }

        const res = await authFetch(`${API_V1}/skills/registry/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            skill_code: formSkillCode,
            skill_name: formSkillName,
            category: formCategory,
            description: formDescription,
            execution_config: {
              source_table: formSourceTable,
              conditions: formConditions,
              condition_logic: formLogic,
            },
          }),
        });
        result = await res.json();
        if (!res.ok) throw new Error(result?.detail?.message || result?.detail?.validation_errors?.join(', ') || `Error ${res.status}`);
      }

      setActionMsg(editingId ? 'Skill updated' : `Skill created: ${result.skill_code || formSkillCode}`);
      setShowBuilder(false);
      resetForm();
      load();
    } catch (e: any) {
      setFormErrors([e.message || 'Save failed']);
    }
    setSaving(false);
  };

  const handleDeactivate = async (skill: SkillDef) => {
    if (!window.confirm(`Deactivate skill "${skill.skill_name}"?`)) return;
    try {
      const res = await authFetch(`${API_V1}/skills/registry/${skill.id}/deactivate`, { method: 'POST' });
      if (!res.ok) throw new Error(`${res.status}`);
      setActionMsg(`Skill ${skill.skill_code} deactivated`);
      setSelectedSkill(null);
      load();
    } catch (e: any) {
      setActionMsg(`Error: ${e.message}`);
    }
  };

  // ── Render ──
  if (loading) return <div style={{ padding: 32 }}><p style={{ color: 'var(--text-secondary)' }}>Loading skill registry...</p></div>;
  if (error) return (
    <div style={{ padding: 32 }}>
      <p style={{ color: 'var(--red)' }}>Error: {error}</p>
      <button onClick={load} style={{ marginTop: 8 }}>Retry</button>
    </div>
  );

  return (
    <div style={{ maxWidth: 1000 }}>
      {actionMsg && (
        <div style={{ padding: '8px 16px', marginBottom: 16, fontSize: 12, background: 'var(--bg-card)', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{actionMsg}</span>
          <button onClick={() => setActionMsg('')} style={{ fontSize: 11, cursor: 'pointer', background: 'none', border: 'none', color: 'var(--text-tertiary)' }}>dismiss</button>
        </div>
      )}

      {/* ═══ VIEW 1: Skills List ═══ */}
      {!showBuilder && !selectedSkill && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Custom Skills</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={openCustomBuilder} style={{ ...btnStyle, fontSize: 11 }}>+ Advanced</button>
              <button onClick={() => openTemplateBuilder(templates[0]?.template_id || '')} style={{ ...btnStyle, background: 'linear-gradient(135deg, var(--navy-900), var(--navy-700))', color: '#fff', border: 'none' }}>+ From Template</button>
            </div>
          </div>

          {/* Template Cards */}
          <div style={{ marginBottom: 24 }}>
            <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Templates</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              {templates.map(t => (
                <div key={t.template_id} style={{
                  padding: '14px 16px', borderRadius: 8,
                  border: '1px solid var(--border-default)', background: 'var(--bg-card)',
                  cursor: 'pointer', transition: 'border-color 0.15s',
                }}
                  onClick={() => openTemplateBuilder(t.template_id)}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--accent)')}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border-default)')}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{t.name}</span>
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3,
                      background: `${CAT_COLORS[t.category] || '#6b7280'}20`,
                      color: CAT_COLORS[t.category] || '#6b7280',
                    }}>{t.category}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8, lineHeight: 1.4 }}>
                    {t.description}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                    {Object.keys(t.parameters).length} parameter{Object.keys(t.parameters).length !== 1 ? 's' : ''}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Client Skills Table */}
          <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Your Custom Skills</h4>
          {skills.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-tertiary)', fontSize: 13, border: '1px solid var(--border-subtle)', borderRadius: 8 }}>
              No custom skills yet. Create one from a template above.
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-default)', fontSize: 11, textTransform: 'uppercase' as const, color: 'var(--text-tertiary)' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left' }}>Code</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left' }}>Name</th>
                    <th style={{ padding: '10px 8px', textAlign: 'center' }}>Category</th>
                    <th style={{ padding: '10px 8px', textAlign: 'center' }}>Status</th>
                    <th style={{ padding: '10px 8px', textAlign: 'center' }}>v</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right' }}>Executions</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right' }}>Success</th>
                  </tr>
                </thead>
                <tbody>
                  {skills.map(s => (
                    <tr key={s.id} style={{ borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }} onClick={() => openDetail(s)}>
                      <td style={{ padding: '8px 12px' }}>
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>{s.skill_code}</div>
                      </td>
                      <td style={{ padding: '8px', fontWeight: 500 }}>{s.skill_name}</td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3, background: `${CAT_COLORS[s.category] || '#6b7280'}20`, color: CAT_COLORS[s.category] || '#6b7280' }}>{s.category}</span>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: s.status === 'ACTIVE' ? 'var(--positive)' : '#ef4444' }}>{s.status}</span>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center', fontFamily: 'monospace', fontSize: 11 }}>v{s.version}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>{s.execution_count}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontFamily: 'monospace' }}>
                        {s.success_rate != null ? `${(s.success_rate * 100).toFixed(0)}%` : '--'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ═══ VIEW 2: Skill Detail ═══ */}
      {selectedSkill && !showBuilder && (
        <div>
          <button onClick={() => setSelectedSkill(null)} style={{ ...btnStyle, marginBottom: 16 }}>Back to Skills</button>
          <div className="card" style={{ padding: 20, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>{selectedSkill.skill_name}</h3>
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>{selectedSkill.skill_code}</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => openEdit(selectedSkill)} style={{ ...btnStyle, fontSize: 11, padding: '4px 10px' }}>Edit</button>
                <button onClick={() => handleDeactivate(selectedSkill)} style={{ ...btnStyle, fontSize: 11, padding: '4px 10px', color: 'var(--red)' }}>Deactivate</button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 16, fontSize: 13 }}>
              <div><span style={{ color: 'var(--text-tertiary)' }}>Category:</span> <span style={{ color: CAT_COLORS[selectedSkill.category] }}>{selectedSkill.category}</span></div>
              <div><span style={{ color: 'var(--text-tertiary)' }}>Status:</span> <span style={{ color: selectedSkill.status === 'ACTIVE' ? 'var(--positive)' : '#ef4444' }}>{selectedSkill.status}</span></div>
              <div><span style={{ color: 'var(--text-tertiary)' }}>Version:</span> v{selectedSkill.version}</div>
              <div><span style={{ color: 'var(--text-tertiary)' }}>Executions:</span> {selectedSkill.execution_count}</div>
              <div><span style={{ color: 'var(--text-tertiary)' }}>Success Rate:</span> {selectedSkill.success_rate != null ? `${(selectedSkill.success_rate * 100).toFixed(1)}%` : 'N/A'}</div>
              <div><span style={{ color: 'var(--text-tertiary)' }}>Source:</span> {selectedSkill.source}</div>
            </div>
            {selectedSkill.description && <p style={{ marginTop: 12, fontSize: 13, color: 'var(--text-secondary)' }}>{selectedSkill.description}</p>}
          </div>

          {/* Version History */}
          {versions.length > 0 && (
            <div className="card" style={{ padding: 20 }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Version History</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-default)', color: 'var(--text-tertiary)', fontSize: 10, textTransform: 'uppercase' as const }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Version</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Change</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left' }}>Notes</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map((v, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>v{v.version}</td>
                      <td style={{ padding: '6px 8px' }}>
                        <span style={{
                          fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3,
                          background: v.change_type === 'CREATED' ? '#f0fdf4' : v.change_type === 'DEACTIVATED' ? '#fef2f2' : 'var(--accent-subtle)',
                          color: v.change_type === 'CREATED' ? 'var(--positive)' : v.change_type === 'DEACTIVATED' ? '#ef4444' : 'var(--navy-700)',
                        }}>{v.change_type}</span>
                      </td>
                      <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>{v.change_notes || '--'}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text-tertiary)' }}>
                        {v.created_at ? new Date(v.created_at).toLocaleDateString() : '--'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ═══ VIEW 3: Skill Builder ═══ */}
      {showBuilder && (
        <div>
          <button onClick={() => { setShowBuilder(false); resetForm(); }} style={{ ...btnStyle, marginBottom: 16 }}>Cancel</button>

          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
              {editingId ? 'Edit Skill' : builderMode === 'template' ? 'Create from Template' : 'Create Custom Skill'}
            </h3>

            {/* Mode toggle (only for new skills) */}
            {!editingId && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <button onClick={() => setBuilderMode('template')} style={{
                  ...btnStyle, fontSize: 11,
                  background: builderMode === 'template' ? 'var(--accent)' : 'transparent',
                  color: builderMode === 'template' ? '#000' : 'inherit',
                  borderColor: builderMode === 'template' ? 'var(--accent)' : 'var(--border-default)',
                }}>Template</button>
                <button onClick={() => setBuilderMode('custom')} style={{
                  ...btnStyle, fontSize: 11,
                  background: builderMode === 'custom' ? 'var(--accent)' : 'transparent',
                  color: builderMode === 'custom' ? '#000' : 'inherit',
                  borderColor: builderMode === 'custom' ? 'var(--accent)' : 'var(--border-default)',
                }}>Custom DSL</button>
              </div>
            )}

            {/* Template Mode */}
            {builderMode === 'template' && !editingId && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Template</label>
                  <select value={formTemplateId} onChange={e => { setFormTemplateId(e.target.value); const tpl = templates.find(t => t.template_id === e.target.value); if (tpl) { setFormSkillName(tpl.name); setFormCategory(tpl.category); } }} style={selectStyle}>
                    <option value="">Select a template...</option>
                    {templates.map(t => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
                  </select>
                </div>

                {formTemplateId && (() => {
                  const tpl = templates.find(t => t.template_id === formTemplateId);
                  if (!tpl) return null;
                  return (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                      {Object.entries(tpl.parameters).map(([key, param]) => (
                        <div key={key}>
                          <label style={labelStyle}>
                            {key.replace(/_/g, ' ')}{param.required ? ' *' : ''}
                            <span style={{ fontSize: 10, color: 'var(--text-dim)', marginLeft: 4 }}>({param.type})</span>
                          </label>
                          {param.enum ? (
                            <select value={formParams[key] ?? param.default ?? ''} onChange={e => setFormParams(p => ({ ...p, [key]: e.target.value }))} style={selectStyle}>
                              {param.enum.map(v => <option key={v} value={v}>{v}</option>)}
                            </select>
                          ) : param.type === 'array' ? (
                            <input
                              value={formParams[key] ?? ''}
                              onChange={e => setFormParams(p => ({ ...p, [key]: e.target.value }))}
                              style={inputStyle}
                              placeholder="Comma-separated values"
                            />
                          ) : (
                            <input
                              type={param.type === 'number' || param.type === 'integer' ? 'number' : 'text'}
                              step={param.type === 'integer' ? '1' : undefined}
                              value={formParams[key] ?? ''}
                              onChange={e => setFormParams(p => ({ ...p, [key]: e.target.value }))}
                              style={inputStyle}
                              placeholder={param.description}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </>
            )}

            {/* Common fields: code + name */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>Skill Code *</label>
                <input value={formSkillCode} onChange={e => setFormSkillCode(e.target.value)} style={inputStyle} placeholder="e.g. HIGH_VALUE_AP" disabled={!!editingId} />
              </div>
              <div>
                <label style={labelStyle}>Skill Name *</label>
                <input value={formSkillName} onChange={e => setFormSkillName(e.target.value)} style={inputStyle} placeholder="Human-readable name" />
              </div>
            </div>

            {/* Custom Mode: category, description, conditions */}
            {(builderMode === 'custom' || editingId) && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                  <div>
                    <label style={labelStyle}>Category</label>
                    <select value={formCategory} onChange={e => setFormCategory(e.target.value)} style={selectStyle}>
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Source Table</label>
                    <select value={formSourceTable} onChange={e => setFormSourceTable(e.target.value)} style={selectStyle}>
                      <option value="">Select table...</option>
                      {SOURCE_TABLES.map(t => <option key={t} value={t}>{t.replace('canonical_', '')}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Description</label>
                  <textarea value={formDescription} onChange={e => setFormDescription(e.target.value)} style={{ ...inputStyle, minHeight: 50, resize: 'vertical' } as React.CSSProperties} placeholder="What does this skill detect?" />
                </div>

                {/* Conditions */}
                <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Conditions</h4>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontSize: 11, color: 'var(--text-tertiary)', marginRight: 8 }}>Logic:</label>
                  <select value={formLogic} onChange={e => setFormLogic(e.target.value)} style={{ ...selectStyle, width: 80 }}>
                    <option value="AND">AND</option>
                    <option value="OR">OR</option>
                  </select>
                </div>
                {formConditions.map((c, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                    <input value={c.field} onChange={e => updateCondition(i, 'field', e.target.value)} style={{ ...inputStyle, width: 140 }} placeholder="field" />
                    <select value={c.operator} onChange={e => updateCondition(i, 'operator', e.target.value)} style={{ ...selectStyle, width: 180 }}>
                      {VALID_OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {!['IS_NULL', 'IS_NOT_NULL'].includes(c.operator) && (
                      <input
                        value={c.value ?? ''}
                        onChange={e => {
                          const v = e.target.value;
                          const num = Number(v);
                          updateCondition(i, 'value', isNaN(num) || v === '' ? v : num);
                        }}
                        style={{ ...inputStyle, width: 140 }}
                        placeholder="value"
                      />
                    )}
                    <button onClick={() => removeCondition(i)} style={{ ...btnStyle, padding: '3px 8px', color: 'var(--red)' }}>X</button>
                  </div>
                ))}
                <button onClick={addCondition} style={{ ...btnStyle, fontSize: 11, marginBottom: 16 }}>+ Add Condition</button>
              </>
            )}

            {/* Errors */}
            {formErrors.length > 0 && (
              <div style={{ padding: '8px 12px', marginBottom: 12, borderRadius: 6, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                {formErrors.map((e, i) => <div key={i} style={{ fontSize: 12, color: '#ef4444' }}>{e}</div>)}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={handleSave} disabled={saving} style={{ ...btnStyle, background: 'linear-gradient(135deg, var(--navy-900), var(--navy-700))', color: '#fff', border: 'none', opacity: saving ? 0.5 : 1 }}>
                {saving ? 'Saving...' : editingId ? 'Update Skill' : 'Create Skill'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
